import { useEffect, useRef, useState } from 'react';
import Loader from '../loader/loader';
import { motion, AnimatePresence } from 'framer-motion';
import classNames from 'classnames';
import './videoPlayer.sass';

type VideoProps = {
  src: string | undefined;
  muted?: boolean;
  loop?: boolean;
  autoPlay?: boolean;
  poster?: string | null;
  volume: number;
  controls?: boolean;
  cover?: boolean;
  onPlay?: (event: React.SyntheticEvent<HTMLVideoElement>, looped: boolean) => void;
  onWaiting?: React.ReactEventHandler<HTMLVideoElement>;
  onWaitingFirstTime?: React.ReactEventHandler<HTMLVideoElement>;
  onFinishedFirstPlaythrough?: (event: React.SyntheticEvent<HTMLVideoElement>, stutterCount: number) => void;
};

// How long into playback to consider the playing event to be a start (log) or a stutter (ignore)
//   In local testing, playing event usually is triggered at ~6ms into the video (0.0006) so 1 second should capture even slow events
//   Use same constant to filter out waiting events to prevent duplicate logging start and waiting dlogs if video somehow stutters at <1sec of playback
const PlayThresholdSec = 1;

export default function VideoPlayer({ src, muted = false, loop = false, autoPlay = false, poster, volume, controls, cover = false, onPlay, onWaiting, onWaitingFirstTime, onFinishedFirstPlaythrough }: VideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasLoopedRef = useRef<boolean>(false);
  const waitCountRef = useRef<number>(0);
  const [isShowingLoader, setIsShowingLoading] = useState<boolean>(true);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(true);

  const handleOnCanPlayThrough: React.ReactEventHandler<HTMLVideoElement> = (event) => {
    setIsShowingLoading(false);
  };

  const handleOnPlaying: React.ReactEventHandler<HTMLVideoElement> = (event) => {
    // Ignore onPlaying event calls when buffering happens and the video stutters
    if (event.currentTarget.currentTime < PlayThresholdSec) {
      onPlay?.(event, isPlaying);
      if (!isPlaying) {
        setIsPlaying(true);
      } else {
        if (!hasLoopedRef.current) {
          onFinishedFirstPlaythrough?.(event, waitCountRef.current);
          hasLoopedRef.current = true;
        }
      }
    }
  };

  const handleOnWaiting: React.ReactEventHandler<HTMLVideoElement> = (event) => {
    // Ignore onWaiting event calls if video somehow stutters at the beginning to not dupe w/ onPlay events
    if (event.currentTarget.currentTime > PlayThresholdSec) {
      onWaiting?.(event);

      if (waitCountRef.current === 0) {
        onWaitingFirstTime?.(event);
      }

      waitCountRef.current++;
    }
  };

  const handleOnPause = () => {
    setIsPlaying(false);
  };

  const togglePlay = () => {
    const video = videoRef?.current;

    if (video) {
      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
    }
  };

  const toggleMute = () => {
    const video = videoRef?.current;

    if (video) {
      if (video.volume === 0) {
        video.volume = volume;
        setIsMuted(false);
      } else {
        video.volume = 0;
        setIsMuted(true);
      }
    }
  };

  const onVisibilityChange = () => {
    const video = videoRef?.current;

    if (document.hidden) {
      video?.pause();
    } else {
      video?.play();
    }
  };

  // Assign Listeners
  useEffect(() => {
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [videoRef]);

  // Set Initial Values
  useEffect(() => {
    const video = videoRef?.current;

    if (video) {
      video.volume = volume;
    }
  }, [volume]);

  return (
    <>
      {controls && (
        <>
          <Loader isShown={isShowingLoader} />
          <button className="video__toggle" onClick={togglePlay}>
            <AnimatePresence>
              {!isPlaying && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                  <img src="https://prod.cdni.callofduty.com/prelogin/media/icon_play.svg" alt="Play" />
                </motion.div>
              )}
            </AnimatePresence>
          </button>
          <button className="video__mute" onClick={toggleMute}>
            <svg className="video__mute" viewBox="0 0 75 75" stroke="#fff" stroke-width="5">
              <path d="m39,14-17,15H6V48H22l17,15z" stroke-linejoin="round" />
              <AnimatePresence>{(!isMuted || volume !== 0) && <motion.path d="m49,26 20,24m0-24-20,24" fill="none" stroke-linecap="round" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} />}</AnimatePresence>
            </svg>
          </button>
        </>
      )}
      <div className={classNames('video__player', cover && 'video__player--cover')}>
        <video ref={videoRef} muted={muted} loop={loop} autoPlay={autoPlay} poster={poster ?? 'https://prod.cdni.callofduty.com/prelogin/transparent.png'} playsInline onCanPlayThrough={handleOnCanPlayThrough} onPlaying={handleOnPlaying} onPause={handleOnPause} onWaiting={handleOnWaiting}>
          <source src={src} type="video/mp4" />
        </video>
      </div>
    </>
  );
}
